export type Lang = "en" | "es";

export type ProductStatus = "Available" | "Now launching" | "Phase II" | "Early access" | "In development" | "Open" | "Internal";

export type Product = {
  name: string;
  tag: string;
  description: string;
  status: ProductStatus;
  bullets: string[];
  ctaLabel: string;
  ctaHref: string;
  layerColor: string;
  // Herramienta interna: aparece en el diagrama y en Architecture, no en la rejilla de Products.
  internal?: boolean;
  // Captura de la web publica del producto (public/products/*.webp, 960x510).
  image?: string;
};

export type Layer = {
  title: string;
  subtitle: string;
  items: string[];
  color: string;
  labelColor: string;
};

const LAYER_STYLES = {
  capture: { color: "border-emerald-200 bg-emerald-50", labelColor: "text-emerald-700" },
  model: { color: "border-indigo-200 bg-indigo-50", labelColor: "text-indigo-700" },
  understand: { color: "border-amber-200 bg-amber-50", labelColor: "text-amber-700" },
  deliver: { color: "border-sky-200 bg-sky-50", labelColor: "text-sky-700" },
};

const PRODUCT_LINKS = {
  HexaForms: "https://www.hexaforms.com/",
  StrataSynth: "https://www.stratasynth.com/",
  LuminoraCore: "https://luminoracore.com/",
  QualiSynth: "https://www.qualisynth.com/",
  ArenaSynth: "https://arenasynth.com/",
  InsightPulse: "https://www.insightpulse.link/",
};

const en = {
  meta: {
    title: "Ereace - AI-Native Product Studio",
    description:
      "Ereace is an AI-native product studio. We build ventures — not services. StrataSynth available; QualiSynth and ArenaSynth now launching; LuminoraCore, HexaForms, InsightPulse live; PsycheGraph in development. AWS, serverless, partnerships only.",
  },
  statusLabels: {
    Available: "Available",
    "Now launching": "Now launching",
    "Phase II": "Phase II",
    "Early access": "Early access",
    "In development": "In development",
    Open: "Open",
    Internal: "Internal tool",
  } as Record<ProductStatus, string>,
  header: {
    tagline: "Human Intelligence Ecosystem",
    nav: {
      uses: "Uses",
      products: "Products",
      how: "How it works",
      founder: "Founder",
      partners: "Partnerships",
      faq: "FAQ",
    },
    talk: "Talk to us",
    toggleMenu: "Toggle menu",
    switchLabel: "ES",
    imageAlt: "website",
    switchTitle: "Leer en español",
  },
  hero: {
    eyebrow: "Human Intelligence Ecosystem",
    title: "Test decisions with synthetic people before you test them with real ones.",
    body:
      "Ereace connects real human signals, synthetic people and the systems built around them, so teams can understand, test and act on human behavior before decisions reach the real world.",
    ctaPrimary: "Explore the system",
    ctaSecondary: "See products",
    diagram: {
      engineLabel: "Engine",
      engineText: "Synthetic people with psychological depth, conditioned by country and grounded in real demographic data.",
      engineAccess: "Dashboard · CLI · Python SDK · HTTP API",
      appsLabel: "Built on StrataSynth",
      qualisynthLabel: "Understand",
      qualisynthText: "Synthetic pre-research for qualitative studies.",
      arenasynthLabel: "Train",
      arenasynthText: "Rehearsal of high-stakes conversations.",
      systemLabel: "The Ereace system",
      layersLabel: "Ecosystem layers",
      layerCapture: "Capture",
      layerAudience: "Audience",
      layerMemory: "Memory",
    },
  },
  uses: {
    eyebrow: "What you can do",
    title: "One engine. Three ways to apply it.",
    items: [
      {
        verb: "Understand",
        lead: "Find out what people think, need, reject or value before you invest in fieldwork.",
        product: "QualiSynth",
        productLine: "Synthetic interviews by segment, with drivers, barriers and needs, and every quote traceable to its transcript.",
        cta: "Explore QualiSynth",
        href: "https://qualisynth.com/",
        tryLabel: "Try it free",
        tryHref: "https://app.qualisynth.com/try/?lang=en",
      },
      {
        verb: "Test",
        lead: "Put products, journeys, messages and conversational agents in front of people who don't always follow the happy path.",
        product: "StrataSynth",
        productLine: "Direct access to the engine: dashboard, CLI, Python SDK and HTTP API, plus public datasets on Hugging Face.",
        cta: "Explore StrataSynth",
        href: "https://stratasynth.com/",
        tryLabel: "Try it live",
        tryHref: "https://stratasynth.com/demo/try",
      },
      {
        verb: "Train",
        lead: "Practice the conversations where what the other person thinks matters, not just the scenario you prepared.",
        product: "ArenaSynth",
        productLine: "Rehearse negotiations and high-stakes decisions against synthetic decision-makers with their own profile and priorities.",
        cta: "Explore ArenaSynth",
        href: "https://arenasynth.com/",
        tryLabel: "Rehearse it free",
        tryHref: "https://arenasynth.com/try/",
      },
    ],
  },
  how: {
    eyebrow: "How it works",
    title: "From real behavior to simulated behavior.",
    steps: [
      { title: "Capture", text: "Collect real human signals." },
      { title: "Model", text: "Build synthetic people with identity and structured behavior." },
      { title: "Simulate", text: "Put them in front of products, messages, systems and conversations." },
      { title: "Compare", text: "Contrast synthetic with real when it matters." },
      { title: "Learn", text: "Turn what you learn into the next hypothesis." },
    ],
    loop: "Real → synthetic → real → synthetic.",
  },
  thesis: {
    eyebrow: "Platform thesis",
    title: "These aren't disconnected products. They're one loop.",
    body:
      "Understanding humans at scale is not a single-tool problem. It requires capturing real signals, modeling human cognition, running research workflows, and converting everything into usable intelligence. Ereace is one architecture expressed through specialized systems — each with its own market role, all reinforcing the same platform story.",
    loopIntro: "The real competitive advantage is the loop: ",
    loop: "capture real → simulate synthetic → compare both → understand better.",
    loopOutro: " No single product can do this. The ecosystem can.",
    isTitle: "What Ereace is",
    is: [
      "A human intelligence ecosystem",
      "A connected architecture of AI systems",
      "A loop between real and synthetic signals",
      "An end-to-end insight platform",
      "Independent products with one shared logic",
    ],
    isNotTitle: "What Ereace is not",
    isNot: [
      "Not a collection of disconnected SaaS bets",
      "Not just survey software",
      "Not only synthetic data generation",
      "Not a fragmented toolkit",
      "Not a development agency or consultancy",
    ],
    customerTitle: "Single customer model",
    customerBody:
      "A customer buys into Ereace, not a single product. Entry point is one product; activation expands across the ecosystem. One account, unified data governance.",
  },
  architecture: {
    eyebrow: "Architecture",
    title: "Capture → Model → Understand → Deliver",
    body: "Each layer plays a different role, but the value emerges from how they connect into one continuous system.",
    layerLabel: "Layer",
  },
  layers: [
    {
      title: "Capture",
      subtitle: "Collect and qualify high-value human signals with intelligent forms and lead workflows.",
      items: ["HexaForms"],
      ...LAYER_STYLES.capture,
    },
    {
      title: "Model",
      subtitle: "Turn human input into structured cognition, synthetic behavior and persistent memory.",
      items: ["StrataSynth", "LuminoraCore"],
      ...LAYER_STYLES.model,
    },
    {
      title: "Understand",
      subtitle: "Activate the ecosystem through synthetic pre-research, high-stakes rehearsal and real-time audience intelligence.",
      items: ["QualiSynth", "InsightPulse", "ArenaSynth"],
      ...LAYER_STYLES.understand,
    },
    {
      title: "Deliver",
      subtitle: "Convert intelligence into reports and decision-ready outputs for teams and clients.",
      items: ["InsightRender"],
      ...LAYER_STYLES.deliver,
    },
  ] as Layer[],
  productsSection: {
    eyebrow: "Products",
    title: "The Ereace system",
    body: "A synthetic simulation core, StrataSynth, with applications built on it, and the infrastructure layers that feed and extend the system.",
    simulationTitle: "Human simulation",
    otherTitle: "Human intelligence infrastructure",
    otherBody: "Layers that capture human signals, read audiences in real time and give AI agents memory, feeding and extending the system.",
  },
  products: [
    {
      name: "StrataSynth",
      image: "/products/stratasynth.webp",
      tag: "Engine",
      description:
        "Simulation engine for synthetic humans. Model how people think, decide and react — before you go to market. Test products, messages and research hypotheses at scale without waiting for real respondents.",
      status: "Available",
      bullets: [
        "Structured psychological profiles (beliefs, emotions, decision patterns)",
        "8 countries · public datasets on Hugging Face (CC BY 4.0)",
        "Access via dashboard, CLI, Python SDK and HTTP API",
      ],
      ctaLabel: "Try StrataSynth",
      ctaHref: PRODUCT_LINKS.StrataSynth,
      layerColor: "text-indigo-700",
    },
    {
      name: "QualiSynth",
      image: "/products/qualisynth.webp",
      tag: "Built on StrataSynth",
      description:
        "Synthetic pre-research for qualitative research agencies, freelancers and in-house insight teams. Test guides, messages and segments with synthetic interviews before fieldwork.",
      status: "Now launching",
      bullets: [
        "Synthetic interviews with psychographic panels",
        "Every quote traceable to its transcript",
        "Automated insight generation and thematic analysis",
        "Agency-grade deliverables (PPTX, PDF, Excel via InsightRender)",
      ],
      ctaLabel: "Learn about QualiSynth",
      ctaHref: PRODUCT_LINKS.QualiSynth,
      layerColor: "text-amber-700",
    },
    {
      name: "ArenaSynth",
      image: "/products/arenasynth.webp",
      tag: "Built on StrataSynth",
      description:
        "Practice against the person, not the scenario. Rehearse negotiations and high-stakes decisions against synthetic decision-makers built on StrataSynth.",
      status: "Now launching",
      bullets: [],
      ctaLabel: "Explore ArenaSynth",
      ctaHref: PRODUCT_LINKS.ArenaSynth,
      layerColor: "text-rose-700",
    },
    {
      name: "HexaForms",
      image: "/products/hexaforms.webp",
      tag: "Capture",
      description:
        "AI-powered forms that capture, qualify, validate and recover leads with built-in intelligence. The input layer of the ecosystem.",
      status: "Early access",
      bullets: [
        "Lead scoring and prioritization (HOT/WARM/COLD)",
        "AI validation, anti-spam, email and phone verification",
        "Field-level recovery for incomplete submissions",
        "Integrations with n8n, Make and Zapier",
      ],
      ctaLabel: "Try HexaForms",
      ctaHref: PRODUCT_LINKS.HexaForms,
      layerColor: "text-emerald-700",
    },
    {
      name: "InsightPulse",
      image: "/products/insightpulse.webp",
      tag: "Understand",
      description:
        "Real-time audience intelligence for events, product launches and live feedback. Transforms open-text signals into ranked priorities and instant decisions.",
      status: "Early access",
      bullets: [
        "Live feedback capture and semantic clustering",
        "Themes, priorities and evidence-backed insights in real time",
        "For product teams, CX teams and live events",
        "Always-on — not periodic surveys",
      ],
      ctaLabel: "Try InsightPulse",
      ctaHref: PRODUCT_LINKS.InsightPulse,
      layerColor: "text-orange-700",
    },
    {
      name: "LuminoraCore",
      image: "/products/luminoracore.webp",
      tag: "Model",
      description:
        "Memory, continuity and behavioral evolution layer for AI agents. Gives AI systems persistent identity across sessions, models and applications.",
      status: "Phase II",
      bullets: [
        "Portable memory and personality as core API primitives",
        "Structured embeddings for cross-session consistency",
        "Longitudinal behavioral evolution and relational dynamics",
        "Developer-first: API + SDK, model-agnostic",
      ],
      ctaLabel: "Explore LuminoraCore",
      ctaHref: PRODUCT_LINKS.LuminoraCore,
      layerColor: "text-violet-700",
    },
    {
      name: "InsightRender",
      tag: "Deliver",
      description:
        "Internal reporting engine behind QualiSynth's PPTX, PDF and Excel deliverables. It turns research results into executive-ready outputs.",
      status: "Internal",
      bullets: [],
      ctaLabel: "",
      ctaHref: "#",
      layerColor: "text-sky-700",
      internal: true,
    },
  ] as Product[],
  founder: {
    eyebrow: "Founder",
    title: "We build the tools we couldn't find.",
    greetingBefore: "Hi, I'm ",
    greetingAfter: ", founder of Ereace.",
    paragraphs: [
      "Ereace was created from a simple principle: instead of adapting to limited tools, we build the infrastructure we actually need — then reuse it across everything we create. Every product in the ecosystem exists because no existing solution solved the problem well enough.",
      "HexaForms wasn't started as a \"form product\". It exists because capture layers without intelligence lose value at every step. QualiSynth exists because research that takes weeks by the time it's done is already outdated. StrataSynth exists because real respondents are expensive, slow and can't be used to test every hypothesis.",
      "These products aren't bets placed in different directions. They're the same problem approached from different angles — and connected into one system.",
    ],
    pills: ["Founder-led", "Architecture-first", "Built from real needs", "Ventures, not services"],
    cardTitle: "Infrastructure before interface.",
    cardItems: [
      { name: "StrataSynth", text: " is the engine: synthetic people with psychological depth, conditioned by country and grounded in real demographic data." },
      { name: "QualiSynth", text: " applies it to research: synthetic pre-research before you fund fieldwork." },
      { name: "ArenaSynth", text: " applies it to practice: rehearse high-stakes conversations before they happen." },
      { name: "HexaForms, InsightPulse and LuminoraCore", text: " are other layers of the ecosystem: capture, audience signals and memory for AI agents." },
    ],
    coreTitle: "Core message",
    coreMessage: "\"One engine. Multiple applications.\"",
  },
  partners: {
    eyebrow: "Partnerships",
    title: "Ventures only. No consulting.",
    body:
      "We partner to build AI-first products — ideally where you bring distribution, domain leverage, or proprietary data. We bring the architecture, the simulation infrastructure, and the research capabilities.",
    yesTitle: "We say yes to",
    yesBadge: "Product partnership",
    yes: [
      "Repeatable SaaS (multi-tenant), not bespoke software",
      "Clear outcome and clear GTM path (yours or jointly)",
      "AI + cloud provides real leverage, not cosmetic \"AI sprinkles\"",
      "Distribution, domain expertise, or data you bring to the table",
    ],
    noTitle: "We don't do",
    noBadge: "No services",
    no: [
      "Hourly development or outsourcing",
      "\"Build this for us\" engagements",
      "Staff augmentation or contractor roles",
      "Traditional consulting",
    ],
    formTitle: "Submit a partnership proposal",
    formSubtitle: "Short and direct. Only if it's product-based.",
    sent: "Sent",
    selective: "Selective",
    formLoading: "Loading form...",
    formErrorBefore: "The form couldn't load right now. Write to us at ",
    formErrorAfter: " and we'll get back to you.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Clear expectations.",
    body: "Common questions about Ereace and how we work.",
    items: [
      {
        q: "Why does Ereace have multiple products instead of focusing on one?",
        a: "Because understanding humans at scale isn't a single-tool problem. Each product solves a specific layer — capture, simulation, research, high-stakes rehearsal (ArenaSynth), real-time intelligence, reporting. They work independently and compound together. The ecosystem's value is greater than the sum of its parts.",
      },
      {
        q: "What is the relationship between StrataSynth and QualiSynth?",
        a: "StrataSynth is the engine — it generates synthetic humans with structured psychology. QualiSynth is the application — it uses StrataSynth to run synthetic pre-research and hybrid studies that combine real and synthetic respondents.",
      },
      {
        q: "What kind of partnerships do you accept?",
        a: "Product-first collaborations where there's real leverage: distribution you bring, domain expertise, proprietary data, or a clear wedge with a GTM path. We build the AI infrastructure; you bring the market position.",
      },
      {
        q: "Do you take client projects, outsourcing or consulting?",
        a: "No. Ereace is not a consultancy or agency. We don't do hourly development, staff augmentation, or bespoke software builds. We build our own products and partner on ventures.",
      },
      {
        q: "What is LuminoraCore?",
        a: "A portable memory and identity layer for AI agents — persistent across sessions, models and applications. It gives AI systems behavioral continuity and evolving personality over time. Currently in Phase II (API + SDK).",
      },
      {
        q: "How does the 'real + synthetic' loop work?",
        a: "Companies collect real signals (via HexaForms, InsightPulse), then simulate synthetic behavior (via StrataSynth), then compare both (via QualiSynth). This loop lets you test hypotheses without waiting for real data every time, and compare both to understand behavior better.",
      },
    ],
  },
  closing: {
    eyebrow: "Ereace",
    title: "Important decisions happen between people.",
    body: "Ereace builds the infrastructure to simulate those interactions before they happen.",
    talk: "Talk to us",
    explore: "Explore the system",
    loopTitle: "Three ways in",
    loop: ["Understand", "Test", "Train"],
  },
  footer: {
    tagline: "Human Intelligence Ecosystem · Cloud · Serverless · AWS",
    privacy: "Privacy Policy",
    rights: "All rights reserved.",
  },
};

export type LandingCopy = typeof en;

const es: LandingCopy = {
  meta: {
    title: "Ereace - Estudio de productos nativos en IA",
    description:
      "Ereace es un estudio de productos nativos en IA. Construimos productos propios, no servicios. StrataSynth disponible; QualiSynth y ArenaSynth en lanzamiento; LuminoraCore, HexaForms e InsightPulse activos; PsycheGraph en desarrollo.",
  },
  statusLabels: {
    Available: "Disponible",
    "Now launching": "Lanzamiento",
    "Phase II": "Fase II",
    "Early access": "Acceso anticipado",
    "In development": "En desarrollo",
    Open: "Abierto",
    Internal: "Herramienta interna",
  },
  header: {
    tagline: "Ecosistema de inteligencia humana",
    nav: {
      uses: "Usos",
      products: "Productos",
      how: "Cómo funciona",
      founder: "Fundador",
      partners: "Colaboraciones",
      faq: "Preguntas",
    },
    talk: "Hablemos",
    toggleMenu: "Abrir o cerrar menú",
    switchLabel: "EN",
    imageAlt: "web",
    switchTitle: "Read in English",
  },
  hero: {
    eyebrow: "Ecosistema de inteligencia humana",
    title: "Prueba tus decisiones con personas sintéticas antes de probarlas con personas reales.",
    body:
      "Ereace conecta señales humanas reales, personas sintéticas y los sistemas construidos a su alrededor, para entender, probar y actuar sobre el comportamiento humano antes de que las decisiones lleguen al mundo real.",
    ctaPrimary: "Explorar el sistema",
    ctaSecondary: "Ver productos",
    diagram: {
      engineLabel: "Motor",
      engineText: "Personas sintéticas con profundidad psicológica, condicionadas por país y basadas en datos demográficos reales.",
      engineAccess: "Dashboard · CLI · SDK de Python · API HTTP",
      appsLabel: "Construido sobre StrataSynth",
      qualisynthLabel: "Entender",
      qualisynthText: "Pre-research sintético para estudios cualitativos.",
      arenasynthLabel: "Entrenar",
      arenasynthText: "Ensayo de conversaciones de alto riesgo.",
      systemLabel: "El sistema Ereace",
      layersLabel: "Capas del ecosistema",
      layerCapture: "Captura",
      layerAudience: "Audiencias",
      layerMemory: "Memoria",
    },
  },
  uses: {
    eyebrow: "Qué puedes hacer",
    title: "Un motor. Tres formas de aplicarlo.",
    items: [
      {
        verb: "Entender",
        lead: "Descubre qué piensan, necesitan, rechazan o valoran las personas antes de invertir en trabajo de campo.",
        product: "QualiSynth",
        productLine: "Entrevistas sintéticas por segmento, con motivadores, barreras y necesidades, y cada cita trazable hasta su transcripción.",
        cta: "Explorar QualiSynth",
        href: "https://qualisynth.com/es/",
        tryLabel: "Pruébalo gratis",
        tryHref: "https://app.qualisynth.com/try/?lang=es",
      },
      {
        verb: "Poner a prueba",
        lead: "Pon productos, recorridos, mensajes y agentes conversacionales frente a personas que no siempre siguen el camino feliz.",
        product: "StrataSynth",
        productLine: "Acceso directo al motor: dashboard, CLI, SDK de Python y API HTTP, más datasets públicos en Hugging Face.",
        cta: "Explorar StrataSynth",
        href: "https://stratasynth.com/es/",
        tryLabel: "Pruébalo en vivo",
        tryHref: "https://stratasynth.com/demo/try",
      },
      {
        verb: "Entrenar",
        lead: "Practica las conversaciones en las que importa lo que piensa la otra persona, no solo el escenario que has preparado.",
        product: "ArenaSynth",
        productLine: "Ensaya negociaciones y decisiones de alto riesgo frente a decisores sintéticos con su propio perfil y prioridades.",
        cta: "Explorar ArenaSynth",
        href: "https://arenasynth.com/es/",
        tryLabel: "Ensáyalo gratis",
        tryHref: "https://arenasynth.com/es/try/",
      },
    ],
  },
  how: {
    eyebrow: "Cómo funciona",
    title: "Del comportamiento real al comportamiento simulado.",
    steps: [
      { title: "Captar", text: "Recoge señales humanas reales." },
      { title: "Modelar", text: "Construye personas sintéticas con identidad y comportamiento estructurado." },
      { title: "Simular", text: "Ponlas frente a productos, mensajes, sistemas y conversaciones." },
      { title: "Comparar", text: "Contrasta lo sintético con lo real cuando importa." },
      { title: "Aprender", text: "Convierte lo aprendido en la siguiente hipótesis." },
    ],
    loop: "Real → sintético → real → sintético.",
  },
  thesis: {
    eyebrow: "Tesis de plataforma",
    title: "No son productos desconectados. Son un único ciclo.",
    body:
      "Entender a las personas a escala no se resuelve con una sola herramienta. Requiere captar señales reales, modelar la cognición humana, ejecutar flujos de investigación y convertirlo todo en inteligencia utilizable. Ereace es una sola arquitectura expresada en sistemas especializados, cada uno con su propio papel en el mercado y todos reforzando la misma historia de plataforma.",
    loopIntro: "La verdadera ventaja competitiva es el ciclo: ",
    loop: "captar lo real → simular lo sintético → comparar ambos → entender mejor.",
    loopOutro: " Ningún producto aislado puede hacerlo. El ecosistema sí.",
    isTitle: "Qué es Ereace",
    is: [
      "Un ecosistema de inteligencia humana",
      "Una arquitectura conectada de sistemas de IA",
      "Un ciclo entre señales reales y sintéticas",
      "Una plataforma de insights de principio a fin",
      "Productos independientes con una lógica compartida",
    ],
    isNotTitle: "Qué no es Ereace",
    isNot: [
      "No es una colección de apuestas SaaS desconectadas",
      "No es solo software de encuestas",
      "No es solo generación de datos sintéticos",
      "No es un conjunto de herramientas fragmentado",
      "No es una agencia de desarrollo ni una consultora",
    ],
    customerTitle: "Un único modelo de cliente",
    customerBody:
      "Un cliente entra en Ereace, no en un único producto. La entrada es un producto; la activación se extiende por el ecosistema. Una sola cuenta y un gobierno del dato unificado.",
  },
  architecture: {
    eyebrow: "Arquitectura",
    title: "Captura → Modelo → Comprensión → Entrega",
    body: "Cada capa cumple una función distinta, pero el valor surge de cómo se conectan en un único sistema continuo.",
    layerLabel: "Capa",
  },
  layers: [
    {
      title: "Captura",
      subtitle: "Recoge y cualifica señales humanas de alto valor con formularios inteligentes y flujos de leads.",
      items: ["HexaForms"],
      ...LAYER_STYLES.capture,
    },
    {
      title: "Modelo",
      subtitle: "Convierte la información humana en cognición estructurada, comportamiento sintético y memoria persistente.",
      items: ["StrataSynth", "LuminoraCore"],
      ...LAYER_STYLES.model,
    },
    {
      title: "Comprensión",
      subtitle: "Activa el ecosistema con pre-research sintético, ensayo de conversaciones de alto riesgo e inteligencia de audiencias en tiempo real.",
      items: ["QualiSynth", "InsightPulse", "ArenaSynth"],
      ...LAYER_STYLES.understand,
    },
    {
      title: "Entrega",
      subtitle: "Convierte la inteligencia en informes y entregables listos para decidir, para equipos y clientes.",
      items: ["InsightRender"],
      ...LAYER_STYLES.deliver,
    },
  ],
  productsSection: {
    eyebrow: "Productos",
    title: "El sistema Ereace",
    body: "Un núcleo de simulación sintética, StrataSynth, con aplicaciones construidas sobre él, y las capas de infraestructura que alimentan y amplían el sistema.",
    simulationTitle: "Simulación humana",
    otherTitle: "Infraestructura de inteligencia humana",
    otherBody: "Capas que captan señales humanas, leen audiencias en tiempo real y dan memoria a los agentes de IA, alimentando y ampliando el sistema.",
  },
  products: [
    {
      name: "StrataSynth",
      image: "/products/stratasynth.webp",
      tag: "Motor",
      description:
        "Motor de simulación de humanos sintéticos. Modela cómo piensan, deciden y reaccionan las personas antes de salir al mercado. Prueba productos, mensajes e hipótesis de investigación a escala sin esperar a encuestados reales.",
      status: "Available",
      bullets: [
        "Perfiles psicológicos estructurados (creencias, emociones, patrones de decisión)",
        "8 países · datasets públicos en Hugging Face (CC BY 4.0)",
        "Acceso por dashboard, CLI, SDK de Python y API HTTP",
      ],
      ctaLabel: "Probar StrataSynth",
      ctaHref: PRODUCT_LINKS.StrataSynth,
      layerColor: "text-indigo-700",
    },
    {
      name: "QualiSynth",
      image: "/products/qualisynth.webp",
      tag: "Construido sobre StrataSynth",
      description:
        "Pre-research sintético para agencias de investigación cualitativa, freelancers y equipos de insights internos. Pon a prueba guías, mensajes y segmentos con entrevistas sintéticas antes del trabajo de campo.",
      status: "Now launching",
      bullets: [
        "Entrevistas sintéticas con paneles psicográficos",
        "Cada cita trazable hasta su transcripción",
        "Generación automática de insights y análisis temático",
        "Entregables de nivel agencia (PPTX, PDF, Excel vía InsightRender)",
      ],
      ctaLabel: "Conocer QualiSynth",
      ctaHref: PRODUCT_LINKS.QualiSynth,
      layerColor: "text-amber-700",
    },
    {
      name: "ArenaSynth",
      image: "/products/arenasynth.webp",
      tag: "Construido sobre StrataSynth",
      description:
        "Practica frente a la persona, no frente al escenario. Ensaya negociaciones y decisiones de alto riesgo frente a decisores sintéticos construidos sobre StrataSynth.",
      status: "Now launching",
      bullets: [],
      ctaLabel: "Explorar ArenaSynth",
      ctaHref: PRODUCT_LINKS.ArenaSynth,
      layerColor: "text-rose-700",
    },
    {
      name: "HexaForms",
      image: "/products/hexaforms.webp",
      tag: "Captura",
      description:
        "Formularios con IA que captan, cualifican, validan y recuperan leads con inteligencia integrada. La capa de entrada del ecosistema.",
      status: "Early access",
      bullets: [
        "Scoring y priorización de leads (HOT/WARM/COLD)",
        "Validación con IA, antispam y verificación de email y teléfono",
        "Recuperación campo a campo de envíos incompletos",
        "Integraciones con n8n, Make y Zapier",
      ],
      ctaLabel: "Probar HexaForms",
      ctaHref: PRODUCT_LINKS.HexaForms,
      layerColor: "text-emerald-700",
    },
    {
      name: "InsightPulse",
      image: "/products/insightpulse.webp",
      tag: "Comprensión",
      description:
        "Inteligencia de audiencias en tiempo real para eventos, lanzamientos de producto y feedback en directo. Convierte señales de texto abierto en prioridades ordenadas y decisiones inmediatas.",
      status: "Early access",
      bullets: [
        "Captura de feedback en directo y agrupación semántica",
        "Temas, prioridades e insights respaldados por evidencia en tiempo real",
        "Para equipos de producto, equipos de CX y eventos en directo",
        "Siempre activo, no encuestas periódicas",
      ],
      ctaLabel: "Probar InsightPulse",
      ctaHref: PRODUCT_LINKS.InsightPulse,
      layerColor: "text-orange-700",
    },
    {
      name: "LuminoraCore",
      image: "/products/luminoracore.webp",
      tag: "Modelo",
      description:
        "Capa de memoria, continuidad y evolución conductual para agentes de IA. Da a los sistemas de IA una identidad persistente entre sesiones, modelos y aplicaciones.",
      status: "Phase II",
      bullets: [
        "Memoria y personalidad portables como primitivas de la API",
        "Embeddings estructurados para mantener la coherencia entre sesiones",
        "Evolución conductual longitudinal y dinámicas relacionales",
        "Pensado para desarrolladores: API + SDK, independiente del modelo",
      ],
      ctaLabel: "Explorar LuminoraCore",
      ctaHref: PRODUCT_LINKS.LuminoraCore,
      layerColor: "text-violet-700",
    },
    {
      name: "InsightRender",
      tag: "Entrega",
      description:
        "Motor interno de informes detrás de los entregables PPTX, PDF y Excel de QualiSynth. Convierte los resultados de la investigación en entregables listos para dirección.",
      status: "Internal",
      bullets: [],
      ctaLabel: "",
      ctaHref: "#",
      layerColor: "text-sky-700",
      internal: true,
    },
  ],
  founder: {
    eyebrow: "Fundador",
    title: "Construimos las herramientas que no encontrábamos.",
    greetingBefore: "Hola, soy ",
    greetingAfter: ", fundador de Ereace.",
    paragraphs: [
      "Ereace nació de un principio sencillo: en lugar de adaptarnos a herramientas limitadas, construimos la infraestructura que realmente necesitamos y la reutilizamos en todo lo que creamos. Cada producto del ecosistema existe porque ninguna solución disponible resolvía el problema lo bastante bien.",
      "HexaForms no nació como un \"producto de formularios\". Existe porque una capa de captura sin inteligencia pierde valor en cada paso. QualiSynth existe porque una investigación que tarda semanas ya está desfasada cuando termina. StrataSynth existe porque los encuestados reales son caros, lentos y no sirven para probar todas las hipótesis.",
      "Estos productos no son apuestas en direcciones distintas. Son el mismo problema abordado desde ángulos diferentes y conectado en un único sistema.",
    ],
    pills: ["Liderado por el fundador", "Arquitectura primero", "Nacido de necesidades reales", "Productos, no servicios"],
    cardTitle: "Infraestructura antes que interfaz.",
    cardItems: [
      { name: "StrataSynth", text: " es el motor: personas sintéticas con profundidad psicológica, condicionadas por país y basadas en datos demográficos reales." },
      { name: "QualiSynth", text: " lo aplica a la investigación: pre-research sintético antes de financiar el trabajo de campo." },
      { name: "ArenaSynth", text: " lo aplica a la práctica: ensayar conversaciones de alto riesgo antes de que ocurran." },
      { name: "HexaForms, InsightPulse y LuminoraCore", text: " son otras capas del ecosistema: captura, señales de audiencia y memoria para agentes de IA." },
    ],
    coreTitle: "Mensaje central",
    coreMessage: "\"Un motor. Múltiples aplicaciones.\"",
  },
  partners: {
    eyebrow: "Colaboraciones",
    title: "Solo productos propios. Sin consultoría.",
    body:
      "Nos asociamos para construir productos nativos en IA, idealmente cuando tú aportas distribución, conocimiento del sector o datos propios. Nosotros aportamos la arquitectura, la infraestructura de simulación y las capacidades de investigación.",
    yesTitle: "Decimos sí a",
    yesBadge: "Alianza de producto",
    yes: [
      "SaaS repetible (multi-tenant), no software a medida",
      "Un resultado claro y un camino claro al mercado (tuyo o conjunto)",
      "IA y cloud que aportan una ventaja real, no un \"toque de IA\" cosmético",
      "Distribución, conocimiento del sector o datos que tú aportas",
    ],
    noTitle: "No hacemos",
    noBadge: "Sin servicios",
    no: [
      "Desarrollo por horas ni outsourcing",
      "Encargos del tipo \"constrúyenos esto\"",
      "Ampliación de plantilla ni puestos de contratista",
      "Consultoría tradicional",
    ],
    formTitle: "Envía una propuesta de colaboración",
    formSubtitle: "Breve y directa. Solo si se basa en producto.",
    sent: "Enviada",
    selective: "Selectivo",
    formLoading: "Cargando el formulario...",
    formErrorBefore: "No hemos podido cargar el formulario. Escríbenos a ",
    formErrorAfter: " y te responderemos.",
  },
  faq: {
    eyebrow: "Preguntas frecuentes",
    title: "Expectativas claras.",
    body: "Preguntas habituales sobre Ereace y cómo trabajamos.",
    items: [
      {
        q: "¿Por qué Ereace tiene varios productos en lugar de centrarse en uno?",
        a: "Porque entender a las personas a escala no se resuelve con una sola herramienta. Cada producto resuelve una capa concreta: captura, simulación, investigación, ensayo de conversaciones de alto riesgo (ArenaSynth), inteligencia en tiempo real e informes. Funcionan de forma independiente y se potencian entre sí. El valor del ecosistema es mayor que la suma de sus partes.",
      },
      {
        q: "¿Qué relación hay entre StrataSynth y QualiSynth?",
        a: "StrataSynth es el motor: genera humanos sintéticos con psicología estructurada. QualiSynth es la aplicación: usa StrataSynth para realizar pre-research sintético y estudios híbridos que combinan encuestados reales y sintéticos.",
      },
      {
        q: "¿Qué tipo de colaboraciones aceptáis?",
        a: "Colaboraciones centradas en producto donde haya una ventaja real: distribución, conocimiento del sector, datos propios o un punto de entrada claro con un camino al mercado. Nosotros construimos la infraestructura de IA; tú aportas la posición de mercado.",
      },
      {
        q: "¿Aceptáis proyectos de clientes, outsourcing o consultoría?",
        a: "No. Ereace no es una consultora ni una agencia. No hacemos desarrollo por horas, ampliación de plantilla ni software a medida. Construimos nuestros propios productos y colaboramos en proyectos de producto.",
      },
      {
        q: "¿Qué es LuminoraCore?",
        a: "Una capa portable de memoria e identidad para agentes de IA, persistente entre sesiones, modelos y aplicaciones. Da a los sistemas de IA continuidad conductual y una personalidad que evoluciona con el tiempo. Actualmente en Fase II (API + SDK).",
      },
      {
        q: "¿Cómo funciona el ciclo \"real + sintético\"?",
        a: "Las empresas recogen señales reales (con HexaForms e InsightPulse), simulan comportamiento sintético (con StrataSynth) y comparan ambos (con QualiSynth). Este ciclo permite probar hipótesis sin esperar cada vez a datos reales, y comparar ambos para entender mejor el comportamiento.",
      },
    ],
  },
  closing: {
    eyebrow: "Ereace",
    title: "Las decisiones importantes ocurren entre personas.",
    body: "Ereace construye la infraestructura para simular esas interacciones antes de que ocurran.",
    talk: "Hablemos",
    explore: "Explorar el sistema",
    loopTitle: "Tres formas de empezar",
    loop: ["Entender", "Poner a prueba", "Entrenar"],
  },
  footer: {
    tagline: "Ecosistema de inteligencia humana · Cloud · Serverless · AWS",
    privacy: "Política de privacidad",
    rights: "Todos los derechos reservados.",
  },
};

export const COPY: Record<Lang, LandingCopy> = { en, es };
